import {
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableColumn,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {InsertableColumnInfo, TableColumnType} from 'notebook/tables/TableConstants';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import toColumnDefinition from '../columns/toColumnDefinition';
import toInsertableColumnInfos from '../columns/toInsertableColumnInfos';
import getSchemasWithColumnGroups from './getSchemasWithColumnGroups';

export default function getInsertableColumnInfos<TTableMetadata, TRowMetadata>({
  adapter,
  structuredTable,
  selectorsById,
  entryId,
  structuredTableInsertionContext,
}: {
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  selectorsById?: {[selectorId: string]: SchemaFieldSelector} | null;
  entryId: string;
  structuredTableInsertionContext: StructuredTableInsertionContextValue;
}): Array<InsertableColumnInfo> {
  const {schemas} = structuredTable;
  const schemasWithColumnGroups = getSchemasWithColumnGroups(
    structuredTable.metadata.schemaMetadata,
    schemas
  );
  if (schemasWithColumnGroups == null) {
    // The user doesn't have permission on one of the schemas
    return [];
  }
  if (selectorsById == null) {
    // selectorsById are not loaded yet
    return [];
  }
  let allSupportedColumns = adapter
    .getAllSupportedColumns({
      entryId,
      metadata: structuredTable.metadata,
      schemas: schemasWithColumnGroups,
      selectorsById,
      structuredTableInsertionContext,
    })
    .map((structuredTableColumn: StructuredTableColumn) => {
      const columnDefinition = toColumnDefinition({
        structuredTableColumn,
        schemas,
        ...structuredTable.metadata,
      });
      return {
        structuredTableColumn,
        columnDefinition,
      };
    });

  // In result tables created before we deprecated PLFs, an EXISTING_BIOENTITY column is used for the PLF.
  // Don't allow inserting a TAG_VALUE column that duplicates the EXISTING_BIOENTITY column.
  //
  // TODO(BNCH-26647): Remove this once we've backfilled all existing tables to change the PLF column to a
  // regular schema field column.
  if (structuredTable.kind === StructuredTableKind.RESULTS) {
    const plf = getPrimaryLinkField(schemas[0] as ResultSchema);
    const existingBioentityColumn = structuredTable.columns.find(
      (column) => column.columnType === TableColumnType.EXISTING_BIOENTITY
    );
    if (plf != null && existingBioentityColumn != null) {
      allSupportedColumns = allSupportedColumns.filter(
        ({structuredTableColumn}) =>
          !(
            structuredTableColumn.columnType === TableColumnType.TAG_VALUE &&
            structuredTableColumn.schemaFieldApiId === plf.apiId
          )
      );
    }
  }

  return toInsertableColumnInfos(allSupportedColumns, structuredTable.columns);
}
